import { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Avatar,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import { Button, FoodsIcon, FoodsIconButton, FoodsTags, FoodsTooltip, Textarea } from "@foodpilot/foods";
import { useForm, Controller } from "react-hook-form";
import { Post, CreatePostData } from "../../types/post.types";
import { useUpdatePost } from "../../../../api/client-api/social/usePosts";
import {
    SocialUploadedFile,
    toAttachmentMetadata,
    useSocialFileUpload,
} from "../../../../api/client-api/social/useSocialFileUpload";
import { UploadedAttachmentsPreview } from "../CreatePost/UploadedAttachmentsPreview";
import { EmojiPicker } from "../CreatePost/EmojiPicker";
import { useTranslation } from "react-i18next";
import { resolveSocialComposerPlaceholder } from "../../utils/socialComposerPlaceholder";
import { useSnackbar } from "src/utils/useSnackbar";

const MAX_POST_LENGTH = 2000;

const getTranslatedVisibilityLabel = (
    visibility: CreatePostData["visibility"],
    t: (key: string, defaultValue: string) => string,
) => {
    switch (visibility) {
        case "private":
            return t("social.create_post.visibility.private", "Private");
        case "company":
            return t("social.create_post.visibility.company", "Company");
        case "public":
        default:
            return t("social.create_post.visibility.public", "Community");
    }
};

interface ExpandedEditPostProps {
    post: Post;
    open: boolean;
    onClose: () => void;
    user: any;
    onSuccess?: () => void;
}

const mapExistingAttachments = (post: Post): SocialUploadedFile[] =>
    (post.attachments ?? []).map((attachment) => ({
        id: attachment.id?.toString() || "",
        url: attachment.url || "",
        name: attachment.fileName || "",
        mimeType: attachment.mimeType || (attachment.type === "document" ? "application/octet-stream" : "image/jpeg"),
        size: attachment.fileSize || 0,
        kind: attachment.type === "document" ? "document" : "image",
    }));

export const ExpandedEditPost = ({ post, open, onClose, user, onSuccess }: ExpandedEditPostProps) => {
    const theme = useTheme();
    const updatePost = useUpdatePost();
    const { uploadFiles, uploading } = useSocialFileUpload();
    const [uploadedFiles, setUploadedFiles] = useState<SocialUploadedFile[]>([]);
    const [detectedUrls, setDetectedUrls] = useState<string[]>([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const documentInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const composerPlaceholder = resolveSocialComposerPlaceholder(t);
    const { control, handleSubmit, watch, reset, setValue, getValues } = useForm<CreatePostData>({
        defaultValues: {
            content: post.content,
            type: post.type,
            visibility: post.visibility,
            topics: post.topics?.map((topic) => `/api/social/topics/${topic.id}`) || [],
            attachments: [],
        },
    });

    const content = watch("content");

    useEffect(() => {
        if (post) {
            reset({
                content: post.content,
                type: post.type,
                visibility: post.visibility,
                topics: post.topics?.map((topic) => `/api/social/topics/${topic.id}`) || [],
                attachments: [],
            });
            setUploadedFiles(mapExistingAttachments(post));
        }
    }, [post, reset]);

    useEffect(() => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const urls = content.match(urlRegex) || [];
        setDetectedUrls(urls);
    }, [content]);

    const onSubmit = async (data: CreatePostData) => {
        try {
            const { topics, ...dataWithoutTopics } = data;
            const attachmentMetadata = toAttachmentMetadata(uploadedFiles);

            await updatePost.mutateAsync({
                id: post.id,
                data: {
                    ...dataWithoutTopics,
                    metadata: {
                        ...attachmentMetadata,
                        links: detectedUrls,
                    },
                },
            });

            reset();
            setUploadedFiles([]);
            setDetectedUrls([]);
            onClose();
            onSuccess?.();
        } catch (error) {
            // Error handled by mutation
        }
    };

    const handleFilesUploaded = (files: SocialUploadedFile[]) => {
        setUploadedFiles((prev) => [...prev, ...files]);
    };

    const handleFileRemoved = (fileId: string) => {
        setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
    };

    const handleSelectedFiles = async (files: File[]) => {
        if (files.length === 0) return;

        try {
            const uploaded = await uploadFiles(files);
            handleFilesUploaded(uploaded);
        } catch (error) {
            setSnackbarProps({
                type: "error",
                message: t("social.create_post.upload_failed", "Failed to upload file. Please try again."),
            });
        }
    };

    const handleEmojiSelect = (emoji: string) => {
        const currentContent = getValues("content") || "";
        setValue("content", currentContent + emoji);
    };

    const handleClose = () => {
        reset();
        setUploadedFiles([]);
        setDetectedUrls([]);
        setShowEmojiPicker(false);
        onClose();
    };

    const isFormValid = content.trim().length > 0;
    const isSubmitDisabled = !isFormValid || updatePost.isLoading || uploading;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            disableEnforceFocus
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: "90vh",
                },
            }}
        >
            <DialogTitle
                sx={{
                    textAlign: "center",
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    position: "relative",
                }}
            >
                <Typography component="span" variant="h6">
                    {t("social.post.edit", "Edit Post")}
                </Typography>
                <Button
                    onClick={handleClose}
                    variant="text"
                    sx={{ position: "absolute", right: theme.spacing(1), top: theme.spacing(1) }}
                >
                    <FoodsIcon icon="close" size={2} />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={(event) => {
                        void handleSelectedFiles(Array.from(event.target.files ?? []));
                        event.currentTarget.value = "";
                    }}
                />
                <input
                    ref={documentInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    multiple
                    style={{ display: "none" }}
                    onChange={(event) => {
                        void handleSelectedFiles(Array.from(event.target.files ?? []));
                        event.currentTarget.value = "";
                    }}
                />

                <Stack direction="row" alignItems="center" gap={2} mb={2}>
                    <Avatar
                        sx={{
                            width: theme.spacing(5),
                            height: theme.spacing(5),
                            bgcolor: theme.palette.primary.main,
                        }}
                    >
                        {user?.firstname?.[0]}
                        {user?.lastname?.[0]}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1">
                            {user?.firstname} {user?.lastname}
                        </Typography>
                        <Controller
                            name="visibility"
                            control={control}
                            render={({ field }) => (
                                <FoodsTags
                                    startIcon={field.value === "private" ? "lock" : "organization"}
                                    label={getTranslatedVisibilityLabel(field.value, t)}
                                    onClick={() => {
                                        const options: CreatePostData["visibility"][] = [
                                            "company",
                                            "public",
                                            "private",
                                        ];
                                        const currentIndex = options.indexOf(field.value);
                                        const nextIndex = (currentIndex + 1) % options.length;
                                        field.onChange(options[nextIndex]);
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Stack>

                <Controller
                    name="content"
                    control={control}
                    render={({ field }) => (
                        <Textarea
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={composerPlaceholder}
                            minRows={4}
                            maxLength={MAX_POST_LENGTH}
                        />
                    )}
                />

                {detectedUrls.length > 0 && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={1}>
                            <FoodsIcon icon="openNewTab" size={1} />
                            <Typography variant="caption" color="text.secondary">
                                {detectedUrls.length === 1 ?
                                    t("social.create_post.link_detected", "Link detected")
                                :   `${detectedUrls.length} ${t("social.create_post.links_detected", "links detected")}`
                                }
                            </Typography>
                        </Stack>
                        {detectedUrls.slice(0, 3).map((url) => (
                            <FoodsTags
                                key={url}
                                label={url.length > 50 ? url.substring(0, 50) + "..." : url}
                                startIcon="openNewTab"
                            />
                        ))}
                    </Box>
                )}

                <UploadedAttachmentsPreview
                    files={uploadedFiles}
                    onRemove={handleFileRemoved}
                    disabled={updatePost.isLoading || uploading}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    justifyContent: "space-between",
                    px: 3,
                    py: 2,
                    backgroundColor: theme.palette.grey[50],
                }}
            >
                <Box display="flex" gap={0.5} sx={{ position: "relative" }}>
                    <FoodsIconButton
                        size="s"
                        icon="gallery"
                        tooltip={{ text: t("social.create_post.toolbar.photo", "Photo") }}
                        buttonProps={{
                            type: "button",
                            onClick: () => imageInputRef.current?.click(),
                            disabled: updatePost.isLoading || uploading,
                        }}
                    />
                    <FoodsIconButton
                        size="s"
                        icon="documentSpace"
                        tooltip={{ text: t("social.create_post.toolbar.document", "Document") }}
                        buttonProps={{
                            type: "button",
                            onClick: () => documentInputRef.current?.click(),
                            disabled: updatePost.isLoading || uploading,
                        }}
                    />
                    <FoodsTooltip title={t("social.create_post.toolbar.emoji", "Emoji")}>
                        <span>
                            <Button
                                variant="icon-s-nobackground"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                disabled={updatePost.isLoading || uploading}
                                aria-label={t("social.create_post.toolbar.emoji", "Emoji")}
                            >
                                <InsertEmoticonIcon sx={{ fontSize: 20 }} />
                            </Button>
                        </span>
                    </FoodsTooltip>
                    {showEmojiPicker && (
                        <EmojiPicker onEmojiSelect={handleEmojiSelect} onClose={() => setShowEmojiPicker(false)} />
                    )}
                </Box>

                <Stack direction="row" gap={1}>
                    <Button variant="secondary" onClick={handleClose} disabled={updatePost.isLoading || uploading}>
                        {t("social.post.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        variant="primary"
                        disabled={isSubmitDisabled}
                        sx={{ minWidth: 120 }}
                    >
                        {updatePost.isLoading || uploading ?
                            t("social.post.saving", "Saving...")
                        :   t("social.post.save_changes", "Save Changes")}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
